import type { PermissionGrants } from "@/lib/permissions/permissionGrantsModel";
import {
  PERMISSION_RULES,
  type PermissionLanding,
  type PermissionRule,
} from "@/lib/permissions/permissionRules";

type DefaultLandingPage =
  | { action: "redirect"; toPath: string; ruleId: string }
  | { action: "none" };

type PermissionRuleWithLanding = PermissionRule & {
  landing: PermissionLanding;
};

const ruleHasLanding = (
  rule: PermissionRule,
): rule is PermissionRuleWithLanding => rule.landing != null;

const patchFullyGrantedInPermissionGrants = (
  patch: Partial<PermissionGrants>,
  permissionGrants: PermissionGrants,
): boolean =>
  (Object.keys(patch) as (keyof PermissionGrants)[]).every(
    (key) => permissionGrants[key] === true,
  );

export const getLandingPage = (
  permissionGrants: PermissionGrants,
  rules: readonly PermissionRule[] = PERMISSION_RULES,
): DefaultLandingPage => {
  const withLanding = rules.filter(ruleHasLanding);
  const candidates = withLanding.filter((rule) =>
    patchFullyGrantedInPermissionGrants(rule.patch, permissionGrants),
  );
  if (candidates.length === 0) {
    return { action: "none" };
  }
  const ordered = [...candidates]
    .map((rule, index) => ({ rule, index }))
    .sort((a, b) => {
      const pa = a.rule.landing.priority;
      const pb = b.rule.landing.priority;
      if (pa !== pb) {
        return pa - pb;
      }
      return a.index - b.index;
    })
    .map(({ rule }) => rule);
  const best = ordered[0];
  if (best == null) {
    return { action: "none" };
  }
  return {
    action: "redirect",
    toPath: best.landing.toPath,
    ruleId: best.id,
  };
};
